import React, { useState, useRef, useEffect, useMemo } from "react";
import cx from "classnames";
import { Button, Flex, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { GetDoc } from "@/Utils/StateLib";
import { FieldInputValuePickerComposite } from "@/appComponents/Blocks/Composites/FieldInputValuePickerComposite/FieldInputValuePickerComposite";
import { MultiClassFieldToggleMenu } from "@/appComponents/Blocks/Menus/FieldToggleMenu/MultiClassFieldToggleMenu";
import { getHoverTipContent, getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import ToolbarPopover from "@/appComponents/Navigation/ContextualToolbar/ToolbarPopover/ToolbarPopover";
import { GetFieldLabel } from "@coreapp/utils/GetFieldLabel";
import { GetAllFieldDefs } from "@/Utils/DataLib";
import { filterInternalFieldDefs } from "@/Utils/FilterInternalFields";

// Default fields for employee filtering
const DEFAULT_FILTER_FIELDS = [
  { id: "Department", className: "Box" },
  { id: "Name", className: "Box" },
  { id: "Position Title", className: "Position" },
  { id: "PositionID", className: "Position" },
  { id: "Position Location", className: "Position" }
];

// Helper to create a composite field identifier
const createCompositeFieldId = (fieldId, className) => {
  return `${className}::${fieldId}`;
};

// Helper to parse a composite field identifier
const parseCompositeFieldId = compositeId => {
  if (!compositeId || typeof compositeId !== "string" || !compositeId.includes("::")) {
    // Handle legacy field IDs that don't have class prefix
    return { className: "Box", fieldId: compositeId };
  }
  const [className, fieldId] = compositeId.split("::");
  return { className, fieldId };
};

// Helper to get field definition by ID for a specific class
const GetFieldDefByClassAndId = (fieldId, className) => {
  const doc = GetDoc();
  const objDB = doc?.GetObjDB();
  const targetClass = objDB?.GetClassByName(className);
  return targetClass?.GetFieldDefByID(fieldId);
};

// Create a default filter block with empty fields
const createDefaultFilterBlock = () => {
  const filterBlock = new QFilterBlock();
  filterBlock.InitFilterBlock("Employee Filter");
  return filterBlock;
};

// Helper to get all field values from a filter block
const getFieldValuesMap = filterBlock => {
  const fieldValueMap = {};

  if (!filterBlock) return fieldValueMap;

  const rules = filterBlock.GetRules();
  if (!rules?.length) return fieldValueMap;

  rules.forEach(rule => {
    const fieldId = rule.GetFieldID();
    const className = rule.GetFieldClassName() || "Box"; // Default to Box if not specified
    const value = rule.GetValue();

    // Create composite field ID
    const compositeFieldId = createCompositeFieldId(fieldId, className);

    if (!fieldId) return;

    // Initialize field entry if it doesn't exist
    if (!fieldValueMap[compositeFieldId]) {
      fieldValueMap[compositeFieldId] = [];
    }

    // Only add values if they exist
    if (value !== null && value !== undefined) {
      // Handle array values from inlist comparison
      if (Array.isArray(value)) {
        // Add each value if not already present
        value.forEach(v => {
          if (!fieldValueMap[compositeFieldId].includes(v)) {
            fieldValueMap[compositeFieldId].push(v);
          }
        });
      } else {
        // Handle single value
        if (!fieldValueMap[compositeFieldId].includes(value)) {
          fieldValueMap[compositeFieldId].push(value);
        }
      }
    }
  });

  return fieldValueMap;
};

// Extract unique composite field IDs from filter block
const getFieldsFromFilterBlock = filterBlock => {
  const fields = new Set();

  if (!filterBlock) return [];

  const rules = filterBlock.GetRules();
  if (!rules?.length) return [];

  rules.forEach(rule => {
    const fieldId = rule.GetFieldID();
    const className = rule.GetFieldClassName() || "Box"; // Default to Box if not specified

    if (fieldId) {
      // Create composite field ID
      const compositeFieldId = createCompositeFieldId(fieldId, className);
      fields.add(compositeFieldId);
    }
  });

  return Array.from(fields);
};

/**
 * Modal component for filtering employee results using QFilterBlock
 */
export const EmployeeFilterPopover = ({ onApplyFilters, currentFilterBlock }) => {
  const valuePopoverRef = useRef(null);
  const fieldPopoverRef = useRef(null);
  const fieldOpenCtrlRef = useRef(null);
  const openCtrlRef = useRef(null);

  // State for tracking the filter block
  const [filterBlock, setFilterBlock] = useState(null);

  // State to track searchable fields separately from filter rules
  const [searchableFields, setSearchableFields] = useState([]);

  // Field value map for UI display (derived from filterBlock)
  const [fieldValueMap, setFieldValueMap] = useState({});

  // Map to store field definitions by composite ID
  const [fieldDefMap, setFieldDefMap] = useState({});

  const closePopover = () => {
    openCtrlRef?.current?.setOpen(false);
  };

  // Apply filters and close modal
  const handleApplyFilters = () => {
    // Pass the filter block to the parent component
    onApplyFilters(filterBlock);
    closePopover();
  };

  // Get all available field definitions
  const allFieldDefs = useMemo(() => {
    return GetAllFieldDefs();
  }, []);

  // Initialize with filter block
  useEffect(() => {
    let newFilterBlock;
    let initialFieldDefs = [];
    let newFieldDefMap = {};

    if (currentFilterBlock) {
      // Clone to avoid modifying the original
      newFilterBlock = currentFilterBlock.Clone();

      // Extract fields from existing filter block
      const compositeFieldIds = getFieldsFromFilterBlock(currentFilterBlock);

      // Get field definitions
      compositeFieldIds.forEach(compositeId => {
        const { className, fieldId } = parseCompositeFieldId(compositeId);
        const fieldDef = GetFieldDefByClassAndId(fieldId, className);
        if (fieldDef) {
          initialFieldDefs.push({
            fieldDef,
            compositeId,
            className,
            fieldId
          });
          newFieldDefMap[compositeId] = fieldDef;
        }
      });
    } else {
      // Create a new filter block
      newFilterBlock = createDefaultFilterBlock();

      // Add default fields to the filter block
      DEFAULT_FILTER_FIELDS.forEach(({ id: fieldId, className }) => {
        const fieldDef = GetFieldDefByClassAndId(fieldId, className);
        if (fieldDef) {
          const fieldName = fieldDef.GetFieldName();
          const compositeId = createCompositeFieldId(fieldId, className);

          // Add to initialFieldDefs
          initialFieldDefs.push({
            fieldDef,
            compositeId,
            className,
            fieldId
          });

          // Add to fieldDefMap
          newFieldDefMap[compositeId] = fieldDef;

          // Add rule to filter block
          const rule = new QRulePair();
          rule.InitRulePair(
            fieldName, // Field name (label)
            fieldId, // Field ID
            className, // Class name (Box or Position)
            "equals", // Operator
            null // Empty value
          );
          newFilterBlock.AddRule(rule);
        }
      });
    }

    // Get composite IDs for searchable fields
    const searchableCompositeIds = initialFieldDefs.map(({ compositeId }) => compositeId);

    setFilterBlock(newFilterBlock);
    setSearchableFields(searchableCompositeIds);
    setFieldDefMap(newFieldDefMap);

    // Extract field values for UI display
    setFieldValueMap(getFieldValuesMap(newFilterBlock));
  }, [currentFilterBlock]);

  // Check if there are any filter values set
  const hasDirtyState = useMemo(() => {
    return Object.values(fieldValueMap).some(values => values?.length > 0);
  }, [fieldValueMap]);

  // Transform allFieldDefs into a flat array and filter internal fields
  const allFieldDefsArray = useMemo(() => {
    const result = [];
    if (allFieldDefs.Box && allFieldDefs.Box.length) {
      result.push(...filterInternalFieldDefs(allFieldDefs.Box));
    }
    if (allFieldDefs.Position && allFieldDefs.Position.length) {
      result.push(...filterInternalFieldDefs(allFieldDefs.Position));
    }
    return result;
  }, [allFieldDefs]);

  // Add a field to searchable fields
  const addField = fieldOption => {
    if (!filterBlock || !fieldOption) return;

    const { fieldDef, compositeId } = fieldOption;

    // Get field information directly from field definition
    const fieldId = fieldDef.GetFieldID();
    const className = fieldDef.GetFieldClassName();
    const fieldName = fieldDef.GetFieldName();

    // Add to searchable fields list
    setSearchableFields(prev => {
      if (prev.includes(compositeId)) return prev;
      return [...prev, compositeId];
    });

    // Add to field def map
    setFieldDefMap(prev => ({
      ...prev,
      [compositeId]: fieldDef
    }));

    // Initialize field in value map if not already there
    setFieldValueMap(prev => {
      if (prev[compositeId]) return prev;
      return {
        ...prev,
        [compositeId]: []
      };
    });

    // Add an empty rule for this field to the filter block
    const updatedFilterBlock = filterBlock.Clone();

    // Check if this field already has a rule
    const rules = updatedFilterBlock.GetRules();
    const hasRule = rules.some(
      rule => rule.GetFieldID() === fieldId && rule.GetFieldClassName() === className
    );

    if (!hasRule) {
      const rule = new QRulePair();
      rule.InitRulePair(
        fieldName, // Field name (label)
        fieldId, // Field ID
        className, // Class name
        "equals", // Operator
        null // Empty value
      );
      updatedFilterBlock.AddRule(rule);
      setFilterBlock(updatedFilterBlock);
    }
  };

  // Remove a field from searchable fields
  const removeField = (field, fieldData) => {
    // Handle both direct compositeId and fieldData object
    let compositeId, fieldId, className;

    if (fieldData && fieldData.field && fieldData.className) {
      // Using fieldData object from the backward compatible component
      fieldId = fieldData.field;
      className = fieldData.className;
      compositeId = createCompositeFieldId(fieldId, className);
    } else if (typeof field === "string" && field.includes("::")) {
      // Using composite ID directly
      compositeId = field;
      const parsed = parseCompositeFieldId(compositeId);
      fieldId = parsed.fieldId;
      className = parsed.className;
    } else {
      // Fallback to legacy format (field name only, assume Box class)
      fieldId = field;
      className = "Box";
      compositeId = createCompositeFieldId(fieldId, className);
    }

    if (!compositeId) return;

    // Remove from searchable fields list
    setSearchableFields(prev => prev.filter(id => id !== compositeId));

    // Remove field values from value map
    setFieldValueMap(prev => {
      const newMap = { ...prev };
      delete newMap[compositeId];
      return newMap;
    });

    // If the field has values in the filter block, remove those rules
    if (filterBlock) {
      const updatedFilterBlock = filterBlock.Clone();

      // Remove all rules for this field
      const rules = updatedFilterBlock.GetRules();
      const rulesToRemove = rules.filter(
        rule => rule.GetFieldID() === fieldId && rule.GetFieldClassName() === className
      );
      rulesToRemove.forEach(rule => {
        updatedFilterBlock.RemoveRule(rule);
      });

      setFilterBlock(updatedFilterBlock);
    }
  };

  // Toggle field in the filter
  const toggleField = option => {
    if (!option || !option.value) return;

    const compositeId = option.value;

    if (searchableFields.includes(compositeId)) {
      removeField(compositeId);
    } else {
      // Find the field definition by composite ID
      const { className, fieldId } = parseCompositeFieldId(compositeId);
      const fieldDef = allFieldDefs[className]?.find(def => def.GetFieldID() === fieldId);

      if (fieldDef) {
        // Create field option object
        const fieldOption = {
          fieldDef,
          compositeId
        };

        addField(fieldOption);
      }
    }
  };

  // Update field values in the filter block

  const updateFieldValues = (compositeId, values) => {
    if (!filterBlock || !compositeId) return;

    const fieldDef = fieldDefMap[compositeId];
    if (!fieldDef) return;

    // Get field information directly from field definition
    const fieldId = fieldDef.GetFieldID();
    const className = fieldDef.GetFieldClassName();
    const fieldName = fieldDef.GetFieldName();

    // Create a clone to work with
    const updatedFilterBlock = filterBlock.Clone();

    // Remove all existing rules for this field
    const rules = updatedFilterBlock.GetRules();
    const rulesToRemove = rules.filter(
      rule => rule.GetFieldID() === fieldId && rule.GetFieldClassName() === className
    );
    rulesToRemove.forEach(rule => {
      updatedFilterBlock.RemoveRule(rule);
    });

    // Add new rules for each value - using a single rule with inlist comparison for multiple values
    if (values && values.length > 0) {
      // Create a new rule
      const rule = new QRulePair();

      if (values.length > 1) {
        // Use inlist comparison for multiple values
        rule.InitRulePair(
          fieldName, // Field name (label)
          fieldId, // Field ID
          className, // Class name
          "inlist", // Operator for multiple values
          values // Array of values
        );
      } else {
        // Use equals comparison for a single value
        rule.InitRulePair(
          fieldName, // Field name (label)
          fieldId, // Field ID
          className, // Class name
          "equals", // Operator for single value
          values[0] // Single value
        );
      }

      updatedFilterBlock.AddRule(rule);
    } else {
      // Add an empty rule if no values
      const rule = new QRulePair();
      rule.InitRulePair(
        fieldName, // Field name (label)
        fieldId, // Field ID
        className, // Class name
        "equals", // Operator
        null // Empty value
      );
      updatedFilterBlock.AddRule(rule);
    }

    // Update the filter block
    setFilterBlock(updatedFilterBlock);

    // Update field value map
    setFieldValueMap(prev => ({
      ...prev,
      [compositeId]: values || []
    }));
  };

  // Handle setting field value
  const handleSetFieldValue = (data, isFieldValuePick = false) => {
    // Handle both legacy field format and new fieldData format
    let field, value, fieldData;

    if (data) {
      field = data.field;
      value = data.value;
      fieldData = data.fieldData;
    }

    if (!field) return;

    // Determine the compositeId based on what's available
    let compositeId;

    if (fieldData && fieldData.fieldDef) {
      // Using the new fieldData format with field definition
      // Extract field ID and class name from field definition
      const fieldId = fieldData.fieldDef.GetFieldID
        ? fieldData.fieldDef.GetFieldID()
        : fieldData.field;
      const className = fieldData.fieldDef.GetFieldClassName
        ? fieldData.fieldDef.GetFieldClassName()
        : fieldData.className || "Box";
      compositeId = createCompositeFieldId(fieldId, className);
    } else if (typeof field === "string" && field.includes("::")) {
      // Field is already a composite ID
      compositeId = field;
    } else {
      // Try to find the field in fieldDefMap by simple matching
      // This is a fallback for legacy code
      const matchingCompositeId = searchableFields.find(cid => {
        // For each searchable field (which is a compositeId)
        // Check if any field definition's name matches our field name
        const fieldDef = fieldDefMap[cid];
        if (fieldDef) {
          return fieldDef.GetFieldName() === field;
        }
        return false;
      });

      if (matchingCompositeId) {
        compositeId = matchingCompositeId;
      } else {
        // As a last resort, assume it's a field ID in Box class
        // This is risky but maintains backward compatibility
        compositeId = createCompositeFieldId(field, "Box");
      }
    }

    let newValues = [];

    if (isFieldValuePick && typeof value === "string") {
      // Toggle the value in the array
      const currentValues = [...(fieldValueMap[compositeId] || [])];
      const valueIndex = currentValues.indexOf(value);

      if (valueIndex >= 0) {
        currentValues.splice(valueIndex, 1);
      } else {
        currentValues.push(value);
      }

      newValues = currentValues;
    } else if (Array.isArray(value)) {
      // Use the array directly
      newValues = value;
    } else if (typeof value === "string") {
      // Split comma-separated values
      newValues =
        value === undefined || value === null
          ? []
          : value
              .split(",")
              .map(v => v.trim())
              .filter(v => v !== undefined);
    }

    // Update the filter block with new values
    updateFieldValues(compositeId, newValues);
  };

  // Handle removing all values for a field
  const handleRemoveItem = (field, fieldData) => {
    // Handle both direct field and fieldData parameter formats
    let compositeId;

    if (fieldData && fieldData.fieldDef) {
      // Using fieldData from backward compatibility
      // Get field ID and class name from the field definition
      const fieldId = fieldData.fieldDef.GetFieldID
        ? fieldData.fieldDef.GetFieldID()
        : fieldData.field;
      const className = fieldData.fieldDef.GetFieldClassName
        ? fieldData.fieldDef.GetFieldClassName()
        : fieldData.className || "Box";
      compositeId = createCompositeFieldId(fieldId, className);
    } else if (typeof field === "string" && field.includes("::")) {
      // Field is already a composite ID
      compositeId = field;
    } else {
      // Try to find the field in fieldDefMap by simple matching
      const matchingCompositeId = searchableFields.find(cid => {
        // For each searchable field (which is a compositeId)
        // Check if any field definition's name matches our field name
        const fieldDef = fieldDefMap[cid];
        if (fieldDef) {
          return fieldDef.GetFieldName() === field;
        }
        return false;
      });

      if (matchingCompositeId) {
        compositeId = matchingCompositeId;
      } else {
        // As a last resort, assume it's a field ID in Box class
        // This is risky but maintains backward compatibility
        compositeId = createCompositeFieldId(field, "Box");
      }
    }

    if (compositeId) {
      updateFieldValues(compositeId, []);
    }
  };

  // Reset all values but keep fields
  const handleResetValues = () => {
    if (!filterBlock || !searchableFields.length) return;

    // Create a new filter block
    const updatedFilterBlock = new QFilterBlock();
    updatedFilterBlock.InitFilterBlock(filterBlock.GetName());

    // Add empty rules for all current searchable fields
    searchableFields.forEach(compositeId => {
      const fieldDef = fieldDefMap[compositeId];
      if (!fieldDef) return;

      // Get field information directly from field definition
      const fieldId = fieldDef.GetFieldID();
      const className = fieldDef.GetFieldClassName();
      const fieldName = fieldDef.GetFieldName();

      const rule = new QRulePair();
      rule.InitRulePair(
        fieldName, // Field name (label)
        fieldId, // Field ID
        className, // Class name
        "equals", // Operator
        null // Empty value
      );
      updatedFilterBlock.AddRule(rule);
    });

    // Update filter block
    setFilterBlock(updatedFilterBlock);

    // Reset field value map to empty arrays for existing fields
    const resetMap = {};
    Object.keys(fieldValueMap).forEach(compositeId => {
      resetMap[compositeId] = [];
    });
    setFieldValueMap(resetMap);
  };

  // Reset to default fields
  const handleResetFields = () => {
    // Create a new filter block
    const updatedFilterBlock = new QFilterBlock();
    updatedFilterBlock.InitFilterBlock("Employee Filter");

    // Convert default fields to composite IDs and field definitions
    const defaultFieldDefs = [];
    const defaultCompositeIds = [];
    const newFieldDefMap = {};

    // Add default empty fields to the filter block
    DEFAULT_FILTER_FIELDS.forEach(({ id: fieldId, className }) => {
      const fieldDef = GetFieldDefByClassAndId(fieldId, className);
      if (fieldDef) {
        const fieldName = fieldDef.GetFieldName();
        const compositeId = createCompositeFieldId(fieldId, className);

        defaultFieldDefs.push(fieldDef);
        defaultCompositeIds.push(compositeId);
        newFieldDefMap[compositeId] = fieldDef;

        const rule = new QRulePair();
        rule.InitRulePair(
          fieldName, // Field name (label)
          fieldId, // Field ID
          className, // Class name
          "equals", // Operator
          null // Empty value
        );
        updatedFilterBlock.AddRule(rule);
      }
    });

    // Update filter block
    setFilterBlock(updatedFilterBlock);
    setSearchableFields(defaultCompositeIds);
    setFieldDefMap(prev => ({ ...prev, ...newFieldDefMap }));

    // Reset field value map to defaults
    const defaultMap = {};
    defaultCompositeIds.forEach(compositeId => {
      defaultMap[compositeId] = [];
    });
    setFieldValueMap(defaultMap);
  };

  // Create fields menu trigger
  const fieldTrigger = (
    <Button
      dataTestId="employee-filter-fields-trigger"
      modifier="secondary"
      type="button"
      size="small"
      iconProps={{ name: "gear" }}
      tooltipProps={{
        ...getHoverTipContent("Filter Fields", "bottom-start")
      }}
      disabled={!getFeatureAccess(enumFeaturesList.advancedSearch)}
    >
      {appstr("Fields")}
    </Button>
  );

  // Check if current fields match default fields
  const isDefaultFields = useMemo(() => {
    // Convert default field descriptors to composite IDs
    const defaultCompositeIds = DEFAULT_FILTER_FIELDS.map(({ id, className }) =>
      createCompositeFieldId(id, className)
    );

    return (
      defaultCompositeIds.length === searchableFields.length &&
      defaultCompositeIds.every(id => searchableFields.includes(id))
    );
  }, [searchableFields]);

  // If filter block not initialized yet, show loading
  if (!filterBlock) {
    return null;
  }

  const overlayProps = {
    headerProps: {
      size: "large",
      labelSize: "medium",
      title: appstr("Employee filter"),
      modifier: "default",
      headerActions: {
        closeAction: closePopover
      }
    },
    className: cx("oc-popover-content", "oc-filter-overlay"),
    footerProps: {
      size: "large",
      buttonSize: "medium",
      footerActions: {
        cancelAction: handleResetValues,
        cancelLabel: appstr("Reset"),
        customAction: {
          onClick: handleApplyFilters,
          buttonProps: {
            modifier: "primary",
            dataTestId: `employee-filter-reset`
          },
          label: appstr("Apply")
        }
      }
    },
    padding: "2px 0",
    sizeProps: {
      maxWidth: 600,
      maxHeight: 500,
      minWidth: 600
    }
  };

  return (
    <ToolbarPopover
      icon={"filter"}
      iconProps={{ size: "medium" }}
      triggerTestId={"employee-list-filter"}
      ariaLabel={"Employee list filter"}
      placement={"bottom-end"}
      otherPopoverProps={{
        offsetOptions: -2,
        matchReferenceWidth: false,
        openCtrlRef: openCtrlRef
      }}
      overlayProps={overlayProps}
    >
      <Flex direction="column" gap="16px" padding={"16px"} width={"100%"} overflow={"hidden"}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text size="small">
            {appstr("Customize your search with various criteria to find the perfect match")}
          </Text>
          <span style={{ flex: "0 0 18%", display: "flex", justifyContent: "flex-end" }}>
            <MultiClassFieldToggleMenu
              dataTestIdPrefix="employee-filter-fields-toggle-"
              small={true}
              selectedFieldIds={searchableFields}
              openCtrlRef={fieldOpenCtrlRef}
              fieldDefs={allFieldDefsArray}
              onChange={toggleField}
              trigger={fieldTrigger}
              listHeader={{
                label: appstr("Show filter fields"),
                postSearchNode: (
                  <Flex width={"100%"} justifyContent={"end"} padding={"4px 8px"}>
                    <Button
                      modifier="secondary"
                      type="button"
                      size="small"
                      disabled={isDefaultFields}
                      onClick={handleResetFields}
                    >
                      {appstr("Reset")}
                    </Button>
                  </Flex>
                )
              }}
              popoverProps={{
                floatingProps: {
                  ref: fieldPopoverRef
                }
              }}
              listProps={{
                optionSize: "small",
                listWidth: "large"
              }}
              optionSize="small"
              position="bottom-end"
              stayAlive={true}
              fillWidth={false}
            />
          </span>
        </Flex>

        <div className={cx("ocn-employee-list-filter-content", "ocn-list-scrollbar")}>
          <Flex direction="column" gap="16px">
            {searchableFields.length === 0 ? (
              <Text size="small" colorModifier="grey" modifier="italic">
                {appstr("No filter fields selected")}
              </Text>
            ) : (
              searchableFields.map(compositeId => {
                const fieldDef = fieldDefMap[compositeId];
                if (!fieldDef) return null;

                const fieldId = fieldDef.GetFieldID();
                const className = fieldDef.GetFieldClassName();
                const fieldLabel = GetFieldLabel(fieldDef, true);
                const values = fieldValueMap[compositeId] || [];

                // Always use showChips mode when we have values to ensure pills are displayed
                const mode = values.length > 0 ? "showChips" : "default";

                // Set selectedItem correctly for the component's pill rendering logic
                let selectedItem = null;

                if (values.length === 1) {
                  // Single value - use an object with label
                  selectedItem = { label: values[0], value: values[0] };
                } else if (values.length > 1) {
                  // Multiple values - use array of objects
                  // The component will only show the first one with a +X count
                  selectedItem = values.map(val => ({ label: val, value: val }));
                }

                return (
                  <FieldInputValuePickerComposite
                    enableEnterKeyInput={true}
                    allowBlockedValueOption
                    key={compositeId}
                    ignorePicklistValues={true}
                    field={compositeId}
                    value={values} // Pass array directly, not joined
                    onChange={handleSetFieldValue}
                    popoverRef={valuePopoverRef}
                    parentFieldDef={fieldDef} // Pass the field definition
                    inputProps={{
                      placeholder: appstr("Type and press enter"),
                      expandable: true,
                      onResetInput: () => handleRemoveItem(compositeId),
                      clearable: values?.length > 0,
                      label: fieldLabel
                    }}
                    dataTestIdPrefix={`employee-filter-field-${fieldId}-${className}-`}
                    mode={mode}
                    selectedItem={selectedItem}
                    onRemoveItem={handleRemoveItem}
                    menuProps={{
                      stayOpenOnOptionSelect: true,
                      value: values
                    }}
                  />
                );
              })
            )}
          </Flex>
        </div>
      </Flex>
    </ToolbarPopover>
  );
};
